import React from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import {
  Container,
  Navbar,
  NavbarBrand,
  Input,
  Label,
  Form,
  FormGroup,
  FormFeedback,
  FormText,
  Select,
  SelectOption,
  Checkbox,
  Button,
} from 'components/apollo';
import { Row, Column } from 'components/grid';
import PhoneInput, { PhoneValidMsg } from 'components/inputs/phone-input2';
import CountrySelect from 'components/selects/country-select';
import CitySelect from 'components/selects/city-select';
import DatePicker from 'components/common/date-picker/date-picker';
import SettingsTooltip from '../../../settings/components/settings-tooltip';
import GoogleRecaptcha from 'components/vendor/google/google-recaptcha';
import RegisterColumns from '../../common/register-columns';
import Constants from '../../common/constans';
import Validation from '../../common/validation';
import CommonValidation from 'utilities/validations/validation';
import RegisterSuccess from '../../components/register-partner-success';
import RegisterError from '../../components/register-error';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfo, faExclamation, faPencilAlt, faCheck } from '@fortawesome/free-solid-svg-icons';
import { fetchRegion } from '../../../../redux/services/api/region/region-services';
import { confirmEmail } from '../../../../redux/services/api/register/register-service';
import iconImage from '../../router/AFRY-Logotype-Horizontal_White.svg';
import './register-partner.css';

const propTypes = {
  register: PropTypes.func.isRequired,
  success: PropTypes.bool.isRequired,
  companydomains: PropTypes.arrayOf(PropTypes.string),
  getAllCompanyDomains: PropTypes.func.isRequired,
  privacyStatementForNetworkers: PropTypes.string.isRequired,
  portalUrl: PropTypes.string.isRequired,
  params: PropTypes.shape({
    languageCode: PropTypes.string,
  }),
};

const defaultProps = {
  companydomains: [],
  params: {},
};

const contentCols = {
  mobile: { size: 12, offset: 0 },
  tablet: { size: 12, offset: 0 },
  desktop: { size: 10, offset: 1 },
  largeDesktop: { size: 10, offset: 1 },
};

class RegisterPartner extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Email step
      email: '',
      emailConfirmed: false,
      emailError: null,
      emailExistsError: null,
      emailEditing: false,
      emailChecking: false,

      // Personal details
      firstName: '',
      lastName: '',
      birthday: '',
      phoneMobile: '46',
      phoneNumberValid: PhoneValidMsg.required,
      phoneCountry: 'SE',
      homeCountryId: Constants.swedenCountryId,
      countryName: 'Sweden',
      geoLocationKey: '',
      streetAddress: '',
      zipCode: '',
      careOf: '',
      linkedin: '',
      region: '',

      // Company details
      companyNotStarted: false,
      organizationNumber: '',
      companyName: '',
      webpage: '',

      // Communications & sales
      communicationResponsible: true,
      salesManager: '',
      phoneMobileSalesManager: '46',
      phoneMobileSalesValid: null,
      emailSalesManager: '',

      // Bottom
      privacyStatement: false,
      captchaCompleted: false,
      showErrors: false,
      submitting: false,
    };
  }

  componentDidMount() {
    const { getAllCompanyDomains } = this.props;
    getAllCompanyDomains();
  }

  // ─── Email step ──────────────────────────────────────────────────────────────

  handleEmailChange = (e) => {
    this.setState({ email: e.target.value.trim(), emailError: null, emailExistsError: null, emailChecking: false });
  };

  handleEmailBlur = async () => {
    const { email } = this.state;
    const { companydomains } = this.props;
    if (!email || !CommonValidation.Email.allowedEmailAndDomain(email, companydomains || [])) {
      return;
    }
    try {
      await confirmEmail(email);
      this.setState({ emailExistsError: 'This email is already registered' });
    } catch (e) {
      this.setState({ emailExistsError: null });
    }
  };

  handleContinue = async () => {
    const { email } = this.state;
    const { companydomains } = this.props;

    if (!CommonValidation.Email.allowedEmailAndDomain(email, companydomains || [])) {
      this.setState({ emailError: 'Please enter a valid email address' });
      return;
    }

    this.setState({ emailChecking: true });
    try {
      await confirmEmail(email);
      this.setState({ emailExistsError: 'This email is already registered', emailChecking: false });
      return;
    } catch (e) {
      this.setState({ emailExistsError: null, emailChecking: false });
    }

    this.setState({ emailConfirmed: true });
  };

  handleEmailEdit = () => {
    this.setState({ emailEditing: true, emailError: null, emailExistsError: null });
  };

  handleEmailEditConfirm = async () => {
    const { email } = this.state;
    const { companydomains } = this.props;

    if (!CommonValidation.Email.allowedEmailAndDomain(email, companydomains || [])) {
      this.setState({ emailError: 'Please enter a valid email address' });
      return;
    }

    this.setState({ emailChecking: true });
    try {
      await confirmEmail(email);
      this.setState({ emailExistsError: 'This email is already registered', emailChecking: false });
      return;
    } catch (e) {
      this.setState({ emailExistsError: null, emailChecking: false, emailEditing: false });
    }
  };

  // ─── Personal details ────────────────────────────────────────────────────────

  handleChange = async (e) => {
    const { name, value, checked, type, label } = e.target;
    const val = type === 'checkbox' ? checked : value;

    if (name === 'homeCountryId') {
      const phoneByCountry = {
        [Constants.swedenCountryId]: { iso: 'SE', dialCode: '46' },
        [Constants.norwayCountryId]: { iso: 'NO', dialCode: '47' },
        [Constants.denmarkCountryId]: { iso: 'DK', dialCode: '45' },
        [Constants.finlandCountryId]: { iso: 'FI', dialCode: '358' },
      };
      const phoneInfo = phoneByCountry[val.toLowerCase()] || { iso: '', dialCode: '' };
      this.setState({
        homeCountryId: val,
        countryName: label || '',
        geoLocationKey: '',
        region: '',
        phoneCountry: phoneInfo.iso,
        phoneMobile: phoneInfo.dialCode,
        phoneMobileSalesManager: phoneInfo.dialCode,
        phoneNumberValid: PhoneValidMsg.required,
        phoneMobileSalesValid: null,
      });
      return;
    }

    if (name === 'geoLocationKey') {
      this.setState({ geoLocationKey: val });
      const { countryName } = this.state;
      fetchRegion(countryName, label || val).then((res) => {
        const regionName =
          res.data && res.data.length > 0 && res.data[0] !== null ? res.data[0].name : '';
        this.setState({ region: regionName });
      });
      return;
    }

    this.setState({ [name]: val });
  };

  handleBirthdateChange = (number) => {
    const format = moment()._locale._longDateFormat.L.split('');
    let temp = number;
    if (
      number.length !== format.length &&
      format[number.length - 1] !== format[number.length]
    ) {
      temp += format[number.length];
    }
    this.setState({ birthday: temp });
  };

  handlePhoneChange = (number) => {
    this.setState({ phoneMobile: number });
  };

  handlePhoneValid = (validMsg) => {
    if (this.state.phoneNumberValid !== validMsg) {
      this.setState({ phoneNumberValid: validMsg });
    }
  };

  // ─── Communications & sales ──────────────────────────────────────────────────

  handleSalesPhoneChange = (number) => {
    this.setState({ phoneMobileSalesManager: number });
  };

  handleSalesPhoneValid = (validMsg) => {
    if (this.state.phoneMobileSalesValid !== validMsg) {
      this.setState({ phoneMobileSalesValid: validMsg });
    }
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  handleCaptchaSuccess = () => {
    this.setState({ captchaCompleted: true });
  };

  handleSubmit = async () => {
    const { register, params, portalUrl } = this.props;
    const {
      email,
      firstName, lastName, birthday,
      phoneMobile, phoneNumberValid, homeCountryId, geoLocationKey,
      streetAddress, zipCode, careOf, linkedin,
      companyNotStarted, organizationNumber, companyName, webpage,
      communicationResponsible, salesManager, phoneMobileSalesManager, phoneMobileSalesValid,
      emailSalesManager,
      privacyStatement, captchaCompleted,
    } = this.state;

    const personValid =
      Validation.Person.firstName(firstName) &&
      Validation.Person.lastName(lastName) &&
      Validation.Person.birthDay(birthday) &&
      phoneNumberValid === PhoneValidMsg.valid &&
      Validation.Person.geoLocationKey(geoLocationKey) &&
      Validation.Person.zipCode(zipCode) &&
      Validation.Person.streetAddress(streetAddress) &&
      Validation.Person.country(homeCountryId) &&
      Validation.Person.linkedin(linkedin);

    const partnerValid =
      (companyNotStarted || Validation.Partner.organizationNumber(organizationNumber)) &&
      (companyNotStarted || Validation.Partner.companyName(companyName)) &&
      Validation.Partner.webpage(webpage) &&
      Validation.Partner.salesManager(communicationResponsible, salesManager) &&
      Validation.Partner.phoneMobileSalesManager(
        communicationResponsible,
        phoneMobileSalesValid === PhoneValidMsg.valid
      ) &&
      Validation.Partner.emailSalesManager(communicationResponsible, emailSalesManager);

    const captchaValid =
      portalUrl === 'https://portalstage.afry.cloud/' || captchaCompleted;

    if (!personValid || !partnerValid || !privacyStatement || !captchaValid) {
      this.setState({ showErrors: true });
      return;
    }

    const languageCode = (params && params.languageCode) || 'sv';
    const nullify = (val) => (typeof val === 'string' && val.trim() === '' ? null : val);

    this.setState({ submitting: true });
    try {
      await register({
        personal: {
          firstName: nullify(firstName),
          lastName: nullify(lastName),
          birthday: nullify(birthday),
          phoneMobile: `+${phoneMobile}`,
          homeCountryId: nullify(homeCountryId),
          geoLocationKey: nullify(geoLocationKey),
          streetAddress: nullify(streetAddress),
          zipCode: nullify(zipCode),
          careOf: nullify(careOf),
          linkedin: nullify(linkedin),
          header: 'Partner',
          selectedLanguage: languageCode,
        },
        partner: {
          companyNotStarted,
          organizationNumber: nullify(organizationNumber),
          companyName: nullify(companyName),
          webpage: nullify(webpage),
          communicationResponsible,
          salesManager: communicationResponsible ? null : nullify(salesManager),
          phoneMobileSalesManager: communicationResponsible ? null : `+${phoneMobileSalesManager}`,
          emailSalesManager: communicationResponsible ? null : nullify(emailSalesManager),
        },
        account: {
          email: nullify(email),
          languageCode,
          privacyStatement,
          captchaCompleted,
        },
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  getPhoneErrorMsg = (valid) => {
    switch (valid) {
      case PhoneValidMsg.required:
        return 'Please enter your phone number';
      case PhoneValidMsg.removeZero:
        return 'Remove the first zero from number';
      default:
        return 'Phone number is invalid';
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  render() {
    const { success, privacyStatementForNetworkers, portalUrl, companydomains } = this.props;
    const {
      email, emailConfirmed, emailError, emailExistsError, emailEditing, emailChecking,
      firstName, lastName, birthday,
      phoneMobile, phoneNumberValid, phoneCountry,
      homeCountryId, geoLocationKey, streetAddress, zipCode, careOf, linkedin, region,
      companyNotStarted, organizationNumber, companyName, webpage,
      communicationResponsible, salesManager, phoneMobileSalesManager, phoneMobileSalesValid,
      emailSalesManager,
      privacyStatement, captchaCompleted, showErrors, submitting,
    } = this.state;

    const wrapperClass = classNames('register-partner-v2');

    return (
      <div className={wrapperClass}>
        <Navbar dark fixed="top" expand="large">
          <Container>
            <NavbarBrand icon={iconImage} />
          </Container>
        </Navbar>

        <Container className="register-mobile-container">
          <Row>
            <Column {...contentCols}>
              <div className="register-partner-v2-content">
                {success ? (
                  <RegisterSuccess />
                ) : (
                  <Form onSubmit={e => e.preventDefault()}>
                    {/* ── Header ── */}
                    <Row>
                      <Column {...RegisterColumns.fullColumn}>
                        <h2>Partner registration</h2>
                        <p style={{ marginTop: '20px' }}>
                          Welcome and thank you for your interest in joining the AFRY Partner Network!
                          Please fill out the form and submit your request to start your journey
                          as a valued Partner!
                        </p>
                        <div className="register-partner-v2-info-box">
                          <span className="register-partner-v2-info-icon"><FontAwesomeIcon icon={faInfo} /></span>
                          <div>
                            <b>Please ensure that:</b>
                            <ul>
                              <li>The email address is entered correctly - spelling etc</li>
                              <li>You have access to this personal email account since a verification email will be sent for finalizing your registration</li>
                              <li>The phone number you have entered is correct - this will be used as a step in the login process</li>
                            </ul>
                          </div>
                        </div>
                      </Column>
                    </Row>

                    {/* ── Email step ── */}
                    <Row>
                      <Column {...RegisterColumns.leftColumn}>
                        <FormGroup>
                          <Label for="email">Email</Label>
                          <div className="register-partner-v2-email-input-wrapper">
                            <Input
                              name="email"
                              value={email}
                              onChange={this.handleEmailChange}
                              onBlur={this.handleEmailBlur}
                              onKeyDown={e => { if (e.key === 'Enter' && (!emailConfirmed || emailExistsError)) this.handleContinue(); }}
                              disabled={emailConfirmed && !emailExistsError && !emailEditing}
                              style={emailExistsError ? { border: '1.5px solid #d47a85' } : undefined}
                            />
                            {emailConfirmed && !emailExistsError && (
                              <button
                                type="button"
                                className="register-partner-v2-email-edit-btn"
                                onClick={emailEditing ? this.handleEmailEditConfirm : this.handleEmailEdit}
                                disabled={emailChecking}
                              >
                                <FontAwesomeIcon icon={emailEditing ? faCheck : faPencilAlt} />
                              </button>
                            )}
                          </div>
                          {emailError && <FormFeedback>{emailError}</FormFeedback>}
                          {showErrors &&
                            !CommonValidation.Email.allowedEmailAndDomain(
                              email,
                              companydomains || []
                            ) && <FormFeedback>Please enter a valid email</FormFeedback>}
                        </FormGroup>
                        {(!emailConfirmed || emailExistsError) && (
                          <div className="register-partner-v2-continue">
                            <Button color="primary" onClick={this.handleContinue} disabled={emailChecking}>
                              Continue
                            </Button>
                          </div>
                        )}
                      </Column>
                    </Row>
                    {emailExistsError && (
                      <Row>
                        <Column {...RegisterColumns.fullColumn}>
                          <div className="register-partner-v2-info-box" style={{ marginTop: '22px' }}>
                            <span className="register-partner-v2-info-icon"><FontAwesomeIcon icon={faExclamation} /></span>
                            <div>
                              <p><span style={{ fontWeight: 'bold' }}>The email address entered already exists</span></p>
                              <p>To log in to your existing profile, please click <a href="https://portal.afry.com/" target="_blank" rel="noopener noreferrer">here</a>.</p>
                              <p>If you need assistance or help proceeding, please contact <a href="mailto:PartnerBusinessSupport@afry.com">PartnerBusinessSupport@afry.com</a>.</p>
                            </div>
                          </div>
                        </Column>
                      </Row>
                    )}

                    {/* ── Rest of form (revealed after email confirmed) ── */}
                    {emailConfirmed && !emailExistsError && (
                      <>
                        {/* ── Contact details ── */}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <h3>Contact details</h3>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="firstName">First name</Label>
                              <Input
                                name="firstName"
                                onChange={this.handleChange}
                                value={firstName}
                              />
                              {showErrors && !Validation.Person.firstName(firstName) && (
                                <FormFeedback>Please enter your first name</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="lastName">Last name</Label>
                              <Input
                                name="lastName"
                                onChange={this.handleChange}
                                value={lastName}
                              />
                              {showErrors && !Validation.Person.lastName(lastName) && (
                                <FormFeedback>Please enter your last name</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="birthDate">Date of birth</Label>
                              <div className="register-partner-v2-field-group">
                                <DatePicker
                                  id="register-v2-date-picker"
                                  date={birthday ? new Date(birthday) : null}
                                  onDateChange={this.handleBirthdateChange}
                                  placeholderText="yyyy-mm-dd"
                                  dateFormat="yyyy-MM-dd"
                                  startDate={false}
                                />
                              </div>
                              {showErrors && !Validation.Person.birthDay(birthday) && (
                                <FormFeedback>Please enter your date of birth</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="phoneMobile">Mobile phone</Label>
                              <div className="register-partner-v2-field-group">
                                <PhoneInput
                                  key={phoneCountry}
                                  onValidChange={this.handlePhoneValid}
                                  value={phoneMobile}
                                  countryCode={phoneCountry ? phoneCountry.toLowerCase() : ''}
                                  onChange={this.handlePhoneChange}
                                />
                              </div>
                              {showErrors && phoneNumberValid !== PhoneValidMsg.valid && (
                                <FormFeedback>
                                  {this.getPhoneErrorMsg(phoneNumberValid)}
                                </FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="streetAddress">Street address</Label>
                              <Input
                                name="streetAddress"
                                onChange={this.handleChange}
                                value={streetAddress}
                              />
                              {showErrors && !Validation.Person.streetAddress(streetAddress) && (
                                <FormFeedback>Please enter your street address</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="careOf">C/O (optional)</Label>
                              <Input name="careOf" onChange={this.handleChange} value={careOf} />
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="zipCode">Zip code</Label>
                              <Input
                                name="zipCode"
                                onChange={this.handleChange}
                                value={zipCode}
                              />
                              {showErrors && !Validation.Person.zipCode(zipCode) && (
                                <FormFeedback>Please enter your zip area code</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="homeCountryId">Country of residence</Label>
                              <div className="register-partner-v2-field-group">
                                <CountrySelect
                                  onChange={this.handleChange}
                                  selectedValue={homeCountryId || ''}
                                  isDisabled={false}
                                />
                              </div>
                              {showErrors && !Validation.Person.country(homeCountryId) && (
                                <FormFeedback>Please select your country of residence</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="geoLocationKey">City of residence</Label>
                              <div className="register-partner-v2-field-group">
                                {!homeCountryId && <Select disabled />}
                                {homeCountryId && (
                                  <CitySelect
                                    onChange={this.handleChange}
                                    selectedValue={geoLocationKey || ''}
                                    countryKey={homeCountryId}
                                    isDisabled={false}
                                  />
                                )}
                              </div>
                              {showErrors && !Validation.Person.geoLocationKey(geoLocationKey) && (
                                <FormFeedback>Please select your city of residence</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="region">Region</Label>
                              <Input name="region" disabled value={region} />
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="linkedin">LinkedIn (optional)</Label>
                              <Input
                                name="linkedin"
                                onChange={this.handleChange}
                                value={linkedin}
                                placeholder="linkedin.com/in/your-profile-name"
                              />
                              {showErrors && !Validation.Person.linkedin(linkedin) && (
                                <FormFeedback>Not a valid LinkedIn URL</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>

                        {/* ── Company details ── */}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <h3>Company details</h3>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.fullColumn}>
                            <FormGroup>
                              <Label for="companyNotStarted">
                                <Checkbox
                                  id="companyNotStarted"
                                  name="companyNotStarted"
                                  onChange={this.handleChange}
                                  checked={companyNotStarted}
                                />
                                {' '}<span style={{ fontSize: '16px' }}>I have not started my company yet</span>
                              </Label>
                            </FormGroup>
                          </Column>
                        </Row>
                        {companyNotStarted && (
                          <Row>
                            <Column {...RegisterColumns.fullColumn}>
                              <div className="register-partner-v2-info-box">
                                <span className="register-partner-v2-info-icon"><FontAwesomeIcon icon={faInfo} /></span>
                                <div>
                                  <p>
                                    You can register as a partner even though you have not started your company yet.
                                  </p>
                                  <p>
                                    Would you like help and support to start your company and/or with the accounting
                                    and administration? Get an offer from{' '}
                                    <a target="_blank" rel="noopener noreferrer" href="https://rebelwork.se/afry">Rebel Work</a>{' '}
                                    by sending an email to{' '}
                                    <a href="mailto:info@rebelwork.se">info@rebelwork.se</a>.
                                  </p>
                                </div>
                              </div>
                            </Column>
                          </Row>
                        )}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="companyName">
                                Company name
                                <SettingsTooltip
                                  content={[
                                    'Company name and company registration number will be used as part of our contract process',
                                  ]}
                                ><span /></SettingsTooltip>
                              </Label>
                              <Input
                                name="companyName"
                                onChange={this.handleChange}
                                value={companyName}
                                disabled={companyNotStarted}
                              />
                              {showErrors && !companyNotStarted && !Validation.Partner.companyName(companyName) && (
                                <FormFeedback>Please enter the company name you are representing</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="organizationNumber">Organization number</Label>
                              <Input
                                name="organizationNumber"
                                onChange={this.handleChange}
                                value={organizationNumber}
                                maxLength="20"
                                disabled={companyNotStarted}
                              />
                              {showErrors && !companyNotStarted &&
                                !Validation.Partner.organizationNumber(organizationNumber) && (
                                  <FormFeedback>Please enter the company registration number</FormFeedback>
                                )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="webpage">Webpage (optional)</Label>
                              <Input
                                name="webpage"
                                onChange={this.handleChange}
                                value={webpage}
                              />
                            </FormGroup>
                          </Column>
                        </Row>

                        {/* ── Communications & sales ── */}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <h3>Communications &amp; sales</h3>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="communicationResponsible">
                                <Checkbox
                                  id="communicationResponsible"
                                  name="communicationResponsible"
                                  onChange={this.handleChange}
                                  checked={communicationResponsible}
                                />
                                {' '}<span style={{ fontSize: '16px' }}>I am responsible for communication with AFRY</span>
                                <SettingsTooltip
                                  content={[
                                    'The communication from AFRY, via email and phone, is directed to the sales responsible. Newsletters will be sent directly to the registered AFRY Network Profile.',
                                  ]}
                                ><span /></SettingsTooltip>
                              </Label>
                            </FormGroup>
                          </Column>
                        </Row>
                        {!communicationResponsible && (
                          <Row>
                            <Column {...RegisterColumns.fullColumn}>
                              <div className="register-partner-v2-info-box">
                                <span className="register-partner-v2-info-icon"><FontAwesomeIcon icon={faInfo} /></span>
                                <div>
                                  <b>Enter the details for your Partner Company Sales Responsible</b>
                                  <p style={{ marginTop: '10px' }}>
                                    The name, phone and email will be used to connect your profile to this sales
                                    responsible person and grant edit access to your profile details to this person.
                                  </p>
                                  <p>
                                    All communication from AFRY, via email and phone, will be directed to the
                                    sales responsible.
                                  </p>
                                </div>
                              </div>
                            </Column>
                          </Row>
                        )}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="salesManager">Sales responsible</Label>
                              <Input
                                name="salesManager"
                                disabled={communicationResponsible}
                                onChange={this.handleChange}
                                value={salesManager}
                                placeholder="First name Last name"
                              />
                              {showErrors &&
                                !Validation.Partner.salesManager(
                                  communicationResponsible,
                                  salesManager
                                ) && (
                                  <FormFeedback>Please fill out the name of your sales responsible here</FormFeedback>
                                )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="phoneMobileSalesManager">
                                Sales responsible phone
                              </Label>
                              <div className="register-partner-v2-field-group">
                                <PhoneInput
                                  key={`sales-${phoneCountry}`}
                                  onValidChange={this.handleSalesPhoneValid}
                                  value={phoneMobileSalesManager}
                                  countryCode={phoneCountry ? phoneCountry.toLowerCase() : 'se'}
                                  onChange={this.handleSalesPhoneChange}
                                  disabled={communicationResponsible}
                                />
                              </div>
                              {showErrors &&
                                !Validation.Partner.phoneMobileSalesManager(
                                  communicationResponsible,
                                  phoneMobileSalesValid === PhoneValidMsg.valid
                                ) && (
                                  <FormFeedback>
                                    Please enter phone for sales responsible
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="emailSalesManager">Sales responsible email</Label>
                              <Input
                                name="emailSalesManager"
                                onChange={this.handleChange}
                                value={emailSalesManager}
                                disabled={communicationResponsible}
                              />
                              {showErrors &&
                                !Validation.Partner.emailSalesManager(
                                  communicationResponsible,
                                  emailSalesManager
                                ) && (
                                  <FormFeedback>
                                    Please enter a valid email for sales responsible
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Column>
                        </Row>

                        {/* ── Privacy notice + Submit ── */}
                        <Row>
                          <Column {...RegisterColumns.fullColumn}>
                            <div className="register-partner-v2-agreement-box">
                              <Label for="privacyStatement" className="register-partner-v2-agreement-label">
                                <Checkbox
                                  id="privacyStatement"
                                  name="privacyStatement"
                                  checked={privacyStatement}
                                  onChange={this.handleChange}
                                />
                                {' '}I have read and agree to the Partner network Terms and conditions and AFRY Privacy notice
                              </Label>
                              <ul className="register-partner-v2-agreement-list">
                                <li>
                                  <a
                                    href="/register/terms"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Terms and conditions
                                  </a>
                                </li>
                                <li>
                                  <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={privacyStatementForNetworkers}
                                  >
                                    AFRY Privacy notice
                                  </a>
                                </li>
                              </ul>
                              {showErrors && !privacyStatement && (
                                <FormFeedback>
                                  You must accept to proceed
                                </FormFeedback>
                              )}
                            </div>
                            <div className="register-partner-v2-captcha-submit">
                              {portalUrl !== 'https://portalstage.afry.cloud/' && privacyStatement && (
                                <FormGroup>
                                  <GoogleRecaptcha
                                    onCaptchaVerified={this.handleCaptchaSuccess}
                                  />
                                  {showErrors && !captchaCompleted && (
                                    <FormFeedback>reCaptcha not valid</FormFeedback>
                                  )}
                                </FormGroup>
                              )}
                              <div className="register-partner-v2-submit">
                                <Button
                                  color="success"
                                  disabled={!privacyStatement || submitting}
                                  onClick={this.handleSubmit}
                                >
                                  {submitting ? (
                                    <>
                                      Sending
                                      <span className="register-partner-v2-dot" style={{ animationDelay: '0s' }}>.</span>
                                      <span className="register-partner-v2-dot" style={{ animationDelay: '0.4s' }}>.</span>
                                      <span className="register-partner-v2-dot" style={{ animationDelay: '0.8s' }}>.</span>
                                    </>
                                  ) : 'Submit'}
                                </Button>
                              </div>
                            </div>
                          </Column>
                        </Row>
                      </>
                    )}
                  </Form>
                )}
                <RegisterError />
              </div>
            </Column>
          </Row>
        </Container>
      </div>
    );
  }
}

RegisterPartner.propTypes = propTypes;
RegisterPartner.defaultProps = defaultProps;

function withParams(Component) {
  return (props) => <Component {...props} params={useParams()} />;
}

export default withParams(RegisterPartner);
